import { useEffect, useState } from 'react';
import { authApi, type SessionUser } from './api';

/**
 * The /login page.
 *
 * A real view, served by the SPA fallback — nginx already maps any unknown path
 * to index.html, so hitting https://host/login directly lands here. The header
 * still carries no login button: this page is reached by URL on purpose.
 *
 * It also handles the forced password change. The bootstrap superuser is
 * created in production with `mustChangePassword`, because its password comes
 * from an .env file and is therefore a shared secret by construction. Without
 * this step that flag would never be acted on and the env password would stay
 * live forever.
 */

const MIN_PASSWORD = 12;

const page: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#F5F8FF', padding: '24px', fontFamily: 'inherit',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: '380px', background: 'white', borderRadius: '10px',
  border: '1px solid #E5E7EB', padding: '28px', boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px',
};
const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: '14px', marginBottom: '14px',
  border: '1px solid #E5E7EB', borderRadius: '6px', outline: 'none',
  fontFamily: 'inherit', background: 'white', color: '#1A1A1A', boxSizing: 'border-box',
};
const button = (disabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '10px', fontSize: '14px', fontWeight: 500,
  background: disabled ? '#9CA3AF' : '#000E9C', color: 'white', border: 'none',
  borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
});
const errorBox: React.CSSProperties = {
  padding: '9px 12px', marginBottom: '14px', borderRadius: '6px',
  background: '#FEF2F2', color: '#B00020', fontSize: '13px',
};

/** Full page load rather than a client-side route change, so useSession re-runs. */
function goToApp(): void {
  window.location.assign('/');
}

export function LoginView() {
  const [checking, setChecking] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set once the credentials are accepted but the account still has to pick a
  // new password before it is usable.
  const [mustChange, setMustChange] = useState<SessionUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // Someone who is already signed in has no business on this page.
  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then((r) => {
        if (cancelled) return;
        if (r.user) goToApp();
        else setChecking(false);
      })
      .catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { user } = await authApi.login(username, password);
      if (user.mustChangePassword) setMustChange(user);
      else goToApp();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (newPassword !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD) {
      setError(`New password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // `password` is still the one just used to sign in — the endpoint
      // re-verifies it so a hijacked session cannot silently change it.
      await fetch('/api/me/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: password, newPassword }),
      }).then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
      });
      goToApp();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div style={page}>
        <div style={{ ...card, textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
          Checking your session…
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <form style={card} onSubmit={mustChange ? submitNewPassword : submitLogin}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{
            width: '34px', height: '34px', background: '#000E9C', borderRadius: '6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: 'white', fontWeight: 600, fontSize: '12px' }}>OVH</span>
          </div>
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A' }}>Quote Tool</div>
            <div style={{ fontSize: '11px', color: '#6B7280' }}>
              {mustChange ? 'Choose a new password' : 'Sign in'}
            </div>
          </div>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        {mustChange ? (
          <>
            <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0, marginBottom: '16px' }}>
              This account still uses the password it was created with. Pick a new one
              to continue — at least {MIN_PASSWORD} characters.
            </p>
            <label style={label} htmlFor="newPassword">New password</label>
            <input
              id="newPassword" type="password" style={input} autoFocus required
              autoComplete="new-password"
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            />
            <label style={label} htmlFor="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword" type="password" style={input} required
              autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
            />
            <button type="submit" style={button(busy)} disabled={busy}>
              {busy ? 'Saving…' : 'Set password and continue'}
            </button>
          </>
        ) : (
          <>
            <label style={label} htmlFor="username">Username</label>
            <input
              id="username" type="text" style={input} autoFocus required
              autoComplete="username" autoCapitalize="none" spellCheck={false}
              value={username} onChange={(e) => setUsername(e.target.value)}
            />
            <label style={label} htmlFor="password">Password</label>
            <input
              id="password" type="password" style={input} required
              autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" style={button(busy)} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </>
        )}

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <a href="/" style={{ fontSize: '12px', color: '#6B7280' }}>
            Continue without signing in
          </a>
        </div>
      </form>
    </div>
  );
}
